//@flow
import React, {Fragment} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import {
  Backdrop,
  Divider,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';
import {Close} from '@material-ui/icons';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import noNotifications from '../../../../../assets/img/no-notifications.png';
import type {StoreNotification} from '../../../../../entities';
import {mapNotificationDetail} from '../../../utils';

import dayjs from 'dayjs';
import 'dayjs/locale/es';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.locale('es');
dayjs.extend(relativeTime);

type Props = {|
  anchorEl: Element,
  handleClose: () => void,
  notifications?: StoreNotification[],
  fetchUnseenNotifications: () => void,
  markNotificationAsSeen: ({notificationId: string}) => void,
|};

const useStyles = makeStyles(theme => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: colors.grey600,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: 320,
    background: 'white',
    boxShadow:
      '0px 0px 1px rgba(181, 181, 181, 0.2), 0px 2px 8px rgba(209, 209, 209, 0.16)',
    borderRadius: '8px',
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      top: '72px !important',
      left: '0px !important',
    },
  },
  row: {
    padding: theme.spacing(0, 2),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontWeight: 600,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.125rem',
    },
  },
  divider: {
    width: 'calc(100% - 32px)',
    margin: '0px auto',
    background: colors.grey300,
  },
  notifsContainer: {
    maxHeight: 400,
    [theme.breakpoints.down('xs')]: {
      maxHeight: 'calc(100vh - 340px)',
    },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  overflowScroll: {
    overflowY: 'overlay',
    textAlign: 'center',
  },
  notification: {
    padding: theme.spacing(1, 2),
    borderRadius: 'unset',
  },
  notLinkNotification: {
    cursor: 'unset',
  },
  notificationContent: {
    width: '100%',
    textAlign: 'left',
    whiteSpace: 'normal',
  },
  text: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  seenText: {
    fontWeight: 400,
  },
  time: {
    marginTop: 4,
    fontSize: '0.875rem',
    color: colors.purple3,
    fontWeight: 500,
  },
  seenTime: {
    color: colors.grey600,
    fontWeight: 400,
  },
  seenBadge: {
    margin: 'auto 8px',
    height: 8,
    width: 8,
    minWidth: 8,
    borderRadius: '4px',
    background: 'transparent',
  },
  seen: {
    background: colors.purple3,
  },
  seeAllButton: {
    margin: theme.spacing(1),
  },
  noNotificationsImg: {
    alignSelf: 'center',
    marginTop: theme.spacing(7),
  },
  noNotificationsText: {
    margin: theme.spacing(3, 0, 7, 0),
    fontWeight: 500,
    color: colors.grey700,
    textAlign: 'center',
  },
}));

export default function NotificationsMenu(props: Props) {
  const classes = useStyles();
  const {
    anchorEl,
    handleClose,
    // fetchUnseenNotifications,
    notifications,
    markNotificationAsSeen,
  } = props;

  const open = Boolean(anchorEl);

  // useEffect(() => {
  //   if (open) fetchUnseenNotifications();
  // }, [fetchUnseenNotifications, open]);

  const mobile = useMediaQuery('(max-width:600px)');

  return (
    <Backdrop
      className={classes.backdrop}
      open={open && mobile}
      onClick={handleClose}
    >
      <Popover
        classes={{paper: classes.root}}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 58,
          horizontal: 56,
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div className={classes.row}>
          <Typography className={classes.title}>Notificaciones</Typography>
          <IconButton onClick={handleClose} edge="end">
            <Close />
          </IconButton>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.notifsContainer}>
          {notifications && notifications.length ? (
            <div className={classes.overflowScroll}>
              {notifications.map((notification, i) => {
                const notificationDetail = mapNotificationDetail(notification);
                if (notificationDetail)
                  return (
                    <Fragment key={`menu-notification-${i}`}>
                      <CustomButton
                        color="default"
                        className={`${classes.notification} ${
                          !notificationDetail.linkType
                            ? classes.notLinkNotification
                            : ''
                        }`}
                        disableRipple={!notificationDetail.linkType}
                        to={
                          notificationDetail.linkType === 'internal' &&
                          notificationDetail.link
                            ? notificationDetail.link
                            : undefined
                        }
                        href={
                          notificationDetail.linkType === 'external' &&
                          notificationDetail.link
                            ? notificationDetail.link
                            : undefined
                        }
                        target={
                          notificationDetail.linkType === 'external'
                            ? '_blank'
                            : undefined
                        }
                        rel={
                          notificationDetail.linkType === 'external'
                            ? 'noopener noreferrer'
                            : undefined
                        }
                        onClick={() =>
                          markNotificationAsSeen({
                            notificationId: notification.id,
                          })
                        }
                      >
                        <div className={classes.notificationContent}>
                          <Typography
                            className={`${classes.text} ${
                              notification.reviewed ? classes.seenText : ''
                            }`}
                          >
                            {notificationDetail.message}
                          </Typography>
                          <Typography
                            className={`${classes.time} ${
                              notification.reviewed ? classes.seenTime : ''
                            }`}
                          >
                            {dayjs(notification.createdAt).fromNow()}
                          </Typography>
                        </div>
                        <div
                          className={`${classes.seenBadge} ${classes.seen}`}
                        />
                      </CustomButton>
                      {i < notifications.length - 1 && (
                        <Divider className={classes.divider} />
                      )}
                    </Fragment>
                  );
                else return null;
              })}
            </div>
          ) : (
            <>
              <img
                className={classes.noNotificationsImg}
                src={noNotifications}
                alt="no notifications"
              />
              <Typography className={classes.noNotificationsText}>
                No tienes notificaciones nuevas
              </Typography>
            </>
          )}
        </div>
        <Divider className={classes.divider} />
        <CustomButton to="/notifications" className={classes.seeAllButton}>
          Ver todas
        </CustomButton>
      </Popover>
    </Backdrop>
  );
}

NotificationsMenu.propTypes = {
  anchorEl: PropTypes.object,
  handleClose: PropTypes.func.isRequired,
  fetchUnseenNotifications: PropTypes.func.isRequired,
  notifications: PropTypes.array,
  markNotificationAsSeen: PropTypes.func.isRequired,
};
